import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { useLang } from "../lib/i18n";
import { IMAGES } from "../lib/images";
import { ALUPLAST_COLORS } from "../lib/aluplastColors";
import { Reveal } from "../lib/motion";
import { Overline } from "../components/ui-vision";

const makeSwatchStyle = (texBase) => (i, col) => ({
  backgroundColor: col.hex,
  backgroundImage: `url(${texBase}${i + 1}.jpg)`,
  backgroundSize: "cover",
  backgroundPosition: "center",
});

export const Systems = ({ s, images, swapTitle = false }) => {
  return (
    <section className="pt-[88px] md:pt-24 pb-10 md:pb-12" data-testid="fenetres-systems">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal className="mb-6 md:mb-8 max-w-3xl">
          <Overline>{s.overline}</Overline>
          {s.catalogueLabel && (
            <a
              href={s.catalogueHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="catalogue-link"
              className="mt-3 inline-block text-sm text-white/50 hover:text-accent-yellow transition-colors duration-300 line-hover"
            >
              {s.catalogueLabel}
            </a>
          )}
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-6">
          {s.items.map((item, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="flex flex-col border-t border-white/15 pt-6" data-testid={`system-${i}`}>
                <div className="overflow-hidden rounded-[14px] h-44 md:h-52 mb-6 bg-white/[0.03] border border-white/10 flex items-center justify-center p-2">
                  <img
                    src={images[i]}
                    alt={item.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-xs uppercase tracking-[0.25em] text-accent-yellow mb-3">
                  {swapTitle
                    ? item.name
                    : item.glazing
                    ? `${item.material} · ${item.glazing}`
                    : item.material}
                </p>
                <h3 className="font-display text-2xl md:text-3xl font-medium mb-4">
                  {swapTitle
                    ? item.glazing
                      ? `${item.material} · ${item.glazing}`
                      : item.material
                    : item.name}
                </h3>
                <p className="text-white/60 font-light text-sm leading-relaxed mb-6">{item.position}</p>
                <p className="text-[11px] uppercase tracking-[0.25em] text-white/35 mb-2">{s.specsLabel}</p>
                <ul>
                  {item.specs.map((sp, j) => (
                    <li key={j} className="flex gap-3 py-2 border-t border-white/10 text-sm text-white/65 leading-snug">
                      <span className="text-accent-yellow shrink-0">—</span>
                      <span>{sp}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const ColourPalette = ({ c, colors = [], texBase = "/aluplast/ap-", pdf = null, downloads = null }) => {
  const swatchStyle = makeSwatchStyle(texBase);
  const [active, setActive] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const INITIAL = 10;
  const list = expanded ? colors : colors.slice(0, INITIAL);
  const dls = downloads || (pdf ? [{ label: c.download, href: pdf }] : []);
  return (
    <section className="pb-16 md:pb-24 max-w-[1440px] mx-auto px-6 md:px-12 border-t border-white/10 pt-8 md:pt-10" data-testid="fenetres-colours">
      <Reveal className="max-w-2xl mb-6 md:mb-8">
        <Overline className="mb-6">{c.overline}</Overline>
        <p className="text-white/60 font-light leading-relaxed">{c.text}</p>
        {c.applies && <p className="mt-3 text-sm text-white/45">{c.applies}</p>}
      </Reveal>

      <Reveal delay={0.1}>
        {colors.length > 0 && (
          <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-10 gap-3 md:gap-4">
            {list.map((col, i) => (
              <motion.button
                layout
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.35 }}
                onClick={() => setActive(i)}
                data-testid={`swatch-${i}`}
                title={col.name}
                className="group text-left"
              >
                <span
                  className="block w-full aspect-square rounded-[10px] border border-white/10 transition-transform duration-300 group-hover:scale-[1.05]"
                  style={swatchStyle(i, col)}
                />
                <span className="mt-2 block text-[11px] text-white/40">{col.code || col.ref}</span>
              </motion.button>
            ))}
          </div>
        )}

        {(dls.length > 0 || colors.length > INITIAL) && (
          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
            {colors.length > INITIAL && (
              <button
                onClick={() => setExpanded((v) => !v)}
                data-testid="colour-toggle"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/20 text-sm font-medium hover:border-accent-yellow hover:text-accent-yellow transition-colors duration-300"
              >
                {expanded ? c.collapse : c.viewAll}
              </button>
            )}
            {dls.map((d, di) => (
              <a
                key={di}
                href={d.href}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid={di === 0 ? "colour-download" : `colour-download-${di}`}
                className="group inline-flex items-center gap-2 text-sm text-accent-yellow"
              >
                <span className="line-hover">{d.label}</span>
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            ))}
          </div>
        )}
      </Reveal>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[120] bg-ink/80 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setActive(null)}
            data-testid="swatch-modal"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="w-full max-w-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-full h-64 rounded-[14px] border border-white/10 bg-cover bg-center" style={swatchStyle(active, list[active])} />
              <div className="mt-4 flex items-start justify-between gap-4">
                <div>
                  <p className="font-display text-lg font-medium">{list[active].name}</p>
                  <p className="text-sm text-white/50 mt-1">{[list[active].code, list[active].ref].filter(Boolean).join(" · ")}</p>
                </div>
                <button onClick={() => setActive(null)} data-testid="swatch-modal-close" className="text-white/50 hover:text-white shrink-0">
                  <X size={18} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default function Fenetres() {
  const { t } = useLang();
  return (
    <>
      <Systems s={t.fenetresPage.systems} images={IMAGES.fenetres.systems} swapTitle />
      <ColourPalette c={t.fenetresPage.colours} colors={ALUPLAST_COLORS} pdf="/aluplast-nuancier.pdf" />
    </>
  );
}
